/* global pendo */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { User } from "winslow-core";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { selectUser } from "../redux/features/appData";

const TRACKING_ID = "G-DB9GMCHNZN";

declare global {
  interface Window {
    _cio: {
      identify: (user: {
        id: string;
        created_at: number;
        first_name: string | null;
        last_name: string | null;

        [key: string]: any; // Allow additional dynamic fields
      }) => void;
    };
  }
}

// If _cio might not be loaded immediately, use this check
const _cio = window._cio || {
  identify: (user: { id: string; created_at: number; [key: string]: any }) => {
    console.warn("Customer.io (_cio) is not loaded.", user);
  },
};

export function identifyUserWithCustomerIO(user: User) {
  if (_cio) {
    if (user && user.email) {
      _cio.identify({
        id: user.email,
        created_at: Math.floor(new Date(user.createdAt).getTime() / 1000),
        first_name: user.firstName,
        last_name: user.lastName,
      });
      console.log("Identified user with Customer.io", user);
    }
  }
}

export const useAnalytics = () => {
  const location = useLocation();
  const storeUser = useSelector(selectUser);

  useEffect(() => {
    const isDebug =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";
    ReactGA.initialize(TRACKING_ID, {
      testMode: isDebug,
    });
  }, []);

  useEffect(() => {
    if (storeUser) {
      const currentPath = location.pathname + location.search;
      const payload = {
        page: currentPath,
        var1: storeUser.id,
        var2: storeUser.username,
      };
      ReactGA.set(payload);

      ReactGA.send({ hitType: "pageview", page: currentPath });
    }
  }, [location]);
};

export enum TRACK_EVENTS {
  CreateWorkspace = "action_workspace_create",
  AddVideoStart = "action_video_start",
  AddVideoEnd = "action_video_end",
  AddLoom = "action_video_loom",
  AddUpload = "action_video_upload",
  OpenRecording = "action_recording_view",
  PlayRecording = "action_recording_play",
  AddComment = "action_recording_comment_new",
  AddArrow = "action_recording_comment_arrow",
  AddEmoji = "action_recording_emoji",
  ViewSprint = "action_sprint_view",
  ViewFeature = "action_feature_view",
  TrackProgress = "action_progress_track",
  UpdateProgress = "action_progress_update",
  ConnectJIRAStart = "action_JIRA_start",
  ConnectJIRAEnd = "action_JIRA_end",
  AddMember = "action_workspace_member_add",
  AddInviteMember = "action_workspace_invite_member",
  RemoveInvite = "action_workspace_remove_invite",
  RemoveMember = "action_workspace_member_remove",
  ViewYourVideoVideos = "action_videos_your_view",
  ViewRecentVideoVideos = "action_videos_own_view",
  ViewYourVideoHome = "action_home_your_view",
  ViewRecentVideoHome = "action_home_own_view",
  PlayVideoHome = "action_home_play",
  PlayVideoFeature = "action_feature_play",
  CopyLinkHome = "action_home_copy",
  CopyLinkRecording = "action_recording_copy",
  ViewAllYour = "action_videos_your_all",
  ViewAllRecent = "action_videos_recent_all",

  //implement all these
  AddLink = "action_link_add",
  EditLink = "action_link_edit",
  AddTag = "action_tag_add",
  LinkExpanded = "action_link_expand",
  LinkOpened = "action_link_open",
  LinkSaved = "action_link_saved",
  ViewMentions = "action_view_mentions",
  ViewLater = "action_view_later",
  ViewStarred = "action_view_starred",
  ViewPinned = "action_view_pinned",
  ViewMember = "action_view_member",
  ViewApp = "action_view_app",
  ViewTag = "action_view_tag",
  ViewPublicTag = "action_view_public_tag",
  ViewSlackChannel = "action_view_slack_channel",
  StartedSlackIntegration = "action_slack_start",
  CompletedSlackIntegration = "action_slack_end",
}

const pendoTrack = (name: TRACK_EVENTS, data: object, user?: User) => {
  if (!!pendo) {
    const pendoReady = pendo.isReady && pendo.isReady();

    const userData = {
      id: user?.id || "anonymous",
      username: user?.username || "",
    };

    if (pendoReady) {
      const pendoPayload = { ...userData, ...data };
      const trackName = name as unknown as string;
      return pendo.track(trackName, pendoPayload);
    }

    console.error("pendo is not ready...figure out why");
  }
};

const getUsername = (storeUser?: User): string => {
  return storeUser ? storeUser.firstName + " " + storeUser.lastName : "";
};

export const trackAnalyticsEvent = (
  event: TRACK_EVENTS,
  data: object,
  user?: User
) => {
  const username = getUsername(user);
  //pendoTrack(event, { ...data, username }, user);
  ReactGA.event({
    category: "winslow",
    action: event,
    label: username,
  });
};

//todo: move these events over to use the method above

export const trackAddLoom = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.AddLoom, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.AddLoom,
    label: "placeholder_value",
  });
};

export const trackAddUpload = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.AddUpload, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.AddUpload,
    label: "placeholder_value",
  });
};

export const trackPlayRecording = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.PlayRecording, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.PlayRecording,
    label: "placeholder_value",
  });
};

export const trackAddEmoji = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.AddEmoji, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.AddEmoji,
    label: "placeholder_value",
  });
};

export const trackUpdateProgress = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.UpdateProgress, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.UpdateProgress,
    label: "placeholder_value",
  });
};

export const trackViewRecentVideos = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.ViewRecentVideoVideos, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.ViewRecentVideoVideos,
    label: "placeholder_value",
  });
};

export const trackPlayedViewHome = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.PlayVideoHome, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.PlayVideoHome,
    label: "placeholder_value",
  });
};

export const trackPlayedVideoFeature = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.PlayVideoFeature, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.PlayVideoFeature,
    label: "placeholder_value",
  });
};

export const trackViewAllRecent = (data: object, user: User) => {
  //pendoTrack(TRACK_EVENTS.ViewAllRecent, data, user);
  ReactGA.event({
    category: "winslow",
    action: TRACK_EVENTS.ViewAllRecent,
    label: "placeholder_value",
  });
};
